import { useEffect, useState } from 'react';
import {
  FGBannerAlert,
  FGButton,
  FGInlineAlert,
  FGTabPanel,
  FGTabs,
  FGTypography,
} from '@fg/design-system';
import { SignInView } from './SignInView';
import { SSOView } from './SSOView';
import { ForgotPasswordView } from './ForgotPasswordView';
import { DemoNoteView } from './DemoNoteView';
import {
  callPiwik,
  forgotPasswordDispatch,
  loginWithIpDispatch,
  userLoginDispatch,
  userSSODispatch,
} from '../services/Login';
import clsx from 'clsx';
import { BroadcastEventType } from '../services/broadcast/broadcastHelper';
import { BroadcastService } from '../services/broadcast/broadcastService';
import { getBannerMessage, MessageType } from '../services/bannerMessageHelper';
import { HeaderFooter } from './HeaderFooter';

type Props = {
  customProps: {
    appCode: string;
    identitySamlBaseUrl: string;
    identityCognitoBaseUrl: string;
    attemptedUrl: string;
    initialUrl: string;
    clientId: string;
    ipAuthenticationCompanyName: string;
    errorMessageCode: string;
    csrfToken: string;
    showSSO: boolean;
    showIpAuth: boolean;
    logoType: string;
    demoLink: string;
    appName: string;
    email: string;
    sruMessage: string;
  };
  broadcastService: BroadcastService;
};

export const Login = ({ customProps, broadcastService }: Props) => {
  const {
    appCode,
    identitySamlBaseUrl,
    identityCognitoBaseUrl,
    attemptedUrl,
    initialUrl,
    clientId,
    ipAuthenticationCompanyName,
    errorMessageCode,
    csrfToken,
    showSSO,
    showIpAuth,
    logoType,
    demoLink,
    appName,
    email,
    sruMessage,
  } = customProps;

  const showDisclaimerFooter = appCode === 'frp';
  const [tabIndex, setTabIndex] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [sruBannerMessage, setSruBannerMessage] = useState('');
  const tabs = [{ label: 'Sign In' }, { label: 'SSO' }, { label: 'Forgot Password' }];

  if (!showSSO) {
    tabs.splice(1, 1);
  }

  const getQueryParamMessage = (
    paramValue: string | null,
    messageType: MessageType,
    queryParam: string | null
  ): string => {
    return paramValue ? getBannerMessage(paramValue, messageType, queryParam) : '';
  };

  const determineMessages = () => {
    const queryParams = new URLSearchParams(window.location.search);
    let urlErrorMessage = '';
    let sruSuccessMessage = '';

    const samlError = queryParams.get('samlError');
    if (appCode !== 'creditSights' && samlError) {
      urlErrorMessage = getBannerMessage(samlError, MessageType.SsoError);
    }

    const messages = [
      { param: queryParams.get('invalidResetPassword'), type: MessageType.SignInError, queryParam: 'invalidResetPassword' },
      { param: queryParams.get('invalidRedirectUri'), type: MessageType.SignInError, queryParam: 'invalidRedirectUri' },
      { param: queryParams.get('type'), type: MessageType.SignInError, queryParam: 'type' },
      { param: errorMessageCode, type: MessageType.SignInError, queryParam: undefined },
      { param: sruMessage, type: MessageType.SruSuccess, queryParam: undefined }
    ];

    for (const { param, type, queryParam } of messages) {
      const message = getQueryParamMessage(param, type, queryParam);
      if (message) {
        if (type === MessageType.SignInError) {
          urlErrorMessage = message;
          if (queryParam === 'invalidResetPassword') {
            const index = tabs.findIndex((ele) => ele.label === 'Forgot Password');
            setTabIndex(index);
          }
        } else if (type === MessageType.SruSuccess) {
          sruSuccessMessage = message;
        }
      }
    }

    return { urlErrorMessage, sruSuccessMessage };
  };

  useEffect(() => {
    const { urlErrorMessage, sruSuccessMessage } = determineMessages();
    setErrorMessage(urlErrorMessage);
    setSruBannerMessage(sruSuccessMessage);

    if (urlErrorMessage.includes('Single Sign-on')) {
      setTabIndex(1);
    }
  }, [errorMessageCode, sruMessage, customProps]);

  useEffect(() => {
    broadcastService.postEvent({ eventType: BroadcastEventType.LOGIN_PAGE_RENDERED });
  }, []);

  const onTabChange = (index: number) => {
    setErrorMessage('');
    setTabIndex(index);
  };

  const loginWithIpCall = () => {
    setErrorMessage('');
    const payload = {
      _csrf: csrfToken,
    };

    loginWithIpDispatch(payload, csrfToken)
      .then((response) => {
        broadcastService.postEvent({ eventType: BroadcastEventType.LOGIN_WITH_IP_ATTEMPT_RESPONSE_RECEIVED });
        if (response.redirect) {
          window.location.href = response.redirect;
        }
        callPiwik(['trackEvent', 'Login-IP', 'Click', 'Successful_Log_In']);
      })
      .catch((error: any) => {
        console.log('error', error);
        setErrorMessage('Log in with network is currently unavailable.');
        callPiwik(['trackEvent', 'Login-IP', 'Click', 'Failed_Log_In']);
      });
  };

  const loginAPICall = (payload) => {
    setErrorMessage('');
    return userLoginDispatch(payload, csrfToken)
      .then((response) => {
        broadcastService.postEvent({ eventType: BroadcastEventType.LOGIN_ATTEMPT_RESPONSE_RECEIVED });
        if (response.error && response.message) {
          callPiwik(['trackEvent', 'Login-Credentials', 'Click', 'Failed_Log_In']);
          setErrorMessage(response.message);
          return false;
        } else {
          return response;
        }
      })
      .catch((error: any) => {
        console.log('error', error);
        callPiwik(['trackEvent', 'Login-Credentials', 'Click', 'Failed_Log_In']);
      });
  };

  const ssoAPICall = (organizationId) => {
    setErrorMessage('');
    return userSSODispatch(organizationId)
      .then((response) => {
        if (response.error && response.message) {
          callPiwik(['trackEvent', 'Login-SSO', 'Click', 'Failed_Log_In']);
          setErrorMessage(response.message);
          return false;
        } else {
          return response;
        }
      })
      .catch((error) => {
        console.log('sso error', error);
        callPiwik(['trackEvent', 'Login-SSO', 'Click', 'Failed_Log_In']);
      });
  };

  const forgotPasswordAPICall = (payload) => {
    setErrorMessage('');
    return forgotPasswordDispatch(payload).catch((error) => {
      console.log('error', error);
    });
  };

  return (
    <>
      <div className="fg-sign-in-story">
        <HeaderFooter logoType={logoType} appCode={appCode} showDisclaimerFooter={showDisclaimerFooter}>
          <div hidden>
            <FGBannerAlert type="warning">
              Due to upcoming maintenance, authentication services will not be available from 8:30 AM to 12:30PM ET on
              October 12th, 2024.
            </FGBannerAlert>
          </div>
          <div className="fg-pd--x5 fg-sign-in-story__content-wrapper" style={{ maxWidth: '800px' }}>
            {showIpAuth && ipAuthenticationCompanyName && (
              <div className="fg-fxd--c fg-ai--c" style={{ maxWidth: '424px', display: 'flex', margin: '0 auto' }}>
                <div className="fg-mg--b6 fg-ta--c" style={{ color: 'var(--fg-primary-black)' }}>
                  <FGTypography tag="h6" style="headline6">
                    Looks like you are on the {ipAuthenticationCompanyName} network. Users on this network can Sign in
                    with 1-Click.
                  </FGTypography>
                </div>
                <FGButton size="xl" className="fg-mg--b10" onClick={loginWithIpCall}>
                  1-Click sign in with {ipAuthenticationCompanyName}
                </FGButton>
                <span className="fg-body3-short fg-mg--b10" style={{ color: 'var(--fg-grey-grey-1000)' }}>
                  OR
                </span>
              </div>
            )}
            <div style={{ maxWidth: '646px', marginLeft: 'auto', marginRight: 'auto' }}>
              {errorMessage && (
                <div className={clsx('fg-ta--c', 'fg-error-banner-story__banner-wrapper')}>
                  <FGInlineAlert type="error">{errorMessage}</FGInlineAlert>
                </div>
              )}
              {sruBannerMessage && (
                <div className={clsx('fg-ta--c', 'fg-error-banner-story__banner-wrapper')}>
                  <FGInlineAlert type="success">{sruBannerMessage}</FGInlineAlert>
                </div>
              )}
              <FGTabs
                tabs={tabs}
                variant="tertiary"
                size="large"
                onClick={onTabChange}
                tabIndex={tabIndex}
                isLastTabRightAligned
              >
                <FGTabPanel>
                  {' '}
                  <SignInView
                    loginAPICall={loginAPICall}
                    setErrorMessage={setErrorMessage}
                    appCode={appCode}
                    email={email}
                  ></SignInView>{' '}
                </FGTabPanel>
                {showSSO && (
                  <FGTabPanel>
                    {' '}
                    <SSOView
                      ssoAPICall={ssoAPICall}
                      identitySamlBaseUrl={identitySamlBaseUrl}
                      identityCognitoBaseUrl={identityCognitoBaseUrl}
                      attemptedUrl={attemptedUrl}
                      initialUrl={initialUrl}
                      clientId={clientId}
                    />{' '}
                  </FGTabPanel>
                )}
                <FGTabPanel>
                  {' '}
                  <ForgotPasswordView
                    forgotPasswordAPICall={forgotPasswordAPICall}
                    setErrorMessage={setErrorMessage}
                    appCode={appCode}
                  />{' '}
                </FGTabPanel>
              </FGTabs>
            </div>
            {demoLink && appName && appCode && (
              <DemoNoteView demoLink={demoLink} appName={appName} appCode={appCode}></DemoNoteView>
            )}
          </div>
        </HeaderFooter>
      </div>
    </>
  );
};